import React, { useState, useEffect } from 'react';
import { api } from '../services/apiClient';
import { Calendar, DollarSign, TrendingUp, Search, Clock, AlertCircle } from 'lucide-react';

export default function CatalystCalendarPage() {
    const [activeTab, setActiveTab] = useState('EARNINGS'); // EARNINGS ou DIVIDENDS
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Dates par défaut : d'aujourd'hui à +7 jours
    const getToday = () => new Date().toISOString().split('T')[0];
    const getNextWeek = () => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        return d.toISOString().split('T')[0];
    };
    
    const [startDate, setStartDate] = useState(getToday());
    const [endDate, setEndDate] = useState(getNextWeek());

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const endpoint = activeTab === 'EARNINGS' ? '/calendars/earnings' : '/calendars/dividends';
                const res = await api.get(`${endpoint}?start_date=${startDate}&end_date=${endDate}`);
                setData(res.data);
            } catch (err) {
                console.error("Erreur de chargement du calendrier", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [activeTab, startDate, endDate]);

    const filteredData = data.filter(item => 
        !searchTerm || item.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatCurrency = (val) => val ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val) : '-';

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-20 font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* HEADER */}
                <div className="bg-slate-900 rounded-3xl p-8 mb-8 text-white shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden">
                    <div className="absolute left-0 top-0 w-64 h-64 bg-emerald-500 rounded-full blur-[80px] opacity-20"></div>
                    <div className="relative z-10 flex items-center gap-4">
                        <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center text-emerald-400 border border-emerald-500/30 shrink-0">
                            <Calendar size={32} />
                        </div>
                        <div>
                            <h1 className="text-3xl font-black">Calendrier des Catalyseurs</h1>
                            <p className="text-emerald-200">Anticipez la volatilité : Résultats financiers et détachements de dividendes.</p>
                        </div>
                    </div>
                </div>

                {/* CONTRÔLES (Onglets & Filtres) */}
                <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm mb-8 flex flex-col lg:flex-row gap-6 items-center justify-between">
                    <div className="flex bg-slate-100 p-1 rounded-xl w-full lg:w-auto shrink-0">
                        <button onClick={() => setActiveTab('EARNINGS')} className={`flex-1 lg:flex-none px-6 py-2.5 rounded-lg text-sm font-black transition-all ${activeTab === 'EARNINGS' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                            <div className="flex items-center justify-center gap-2"><TrendingUp size={16}/> Résultats (Earnings)</div>
                        </button>
                        <button onClick={() => setActiveTab('DIVIDENDS')} className={`flex-1 lg:flex-none px-6 py-2.5 rounded-lg text-sm font-black transition-all ${activeTab === 'DIVIDENDS' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                            <div className="flex items-center justify-center gap-2"><DollarSign size={16}/> Dividendes</div>
                        </button>
                    </div>

                    <div className="flex flex-col md:flex-row gap-4 w-full lg:w-auto">
                        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1 w-full md:w-auto">
                            <Clock size={16} className="text-slate-400" />
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent border-none text-sm font-bold text-slate-700 focus:ring-0 cursor-pointer outline-none" />
                            <span className="text-slate-400 font-bold">au</span>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent border-none text-sm font-bold text-slate-700 focus:ring-0 cursor-pointer outline-none" />
                        </div>
                        <div className="relative w-full md:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                            <input type="text" placeholder="Rechercher un Ticker..." className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-indigo-500 transition-all" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                        </div>
                    </div>
                </div>

                {/* TABLEAUX */}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                                {activeTab === 'EARNINGS' ? (
                                    <tr>
                                        <th className="px-6 py-5 font-black uppercase tracking-wider text-xs">Date & Heure</th>
                                        <th className="px-6 py-5 font-black uppercase tracking-wider text-xs">Entreprise</th>
                                        <th className="px-6 py-5 font-black uppercase tracking-wider text-xs text-right">BPA Estimé (EPS)</th>
                                        <th className="px-6 py-5 font-black uppercase tracking-wider text-xs text-right">Revenus Estimés</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        <th className="px-6 py-5 font-black uppercase tracking-wider text-xs">Ex-Date (Détachement)</th>
                                        <th className="px-6 py-5 font-black uppercase tracking-wider text-xs">Entreprise</th>
                                        <th className="px-6 py-5 font-black uppercase tracking-wider text-xs text-right">Dividende</th>
                                        <th className="px-6 py-5 font-black uppercase tracking-wider text-xs text-center">Date de Paiement</th>
                                    </tr>
                                )}
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading ? (
                                    <tr><td colSpan="4" className="px-6 py-12 text-center text-slate-500 font-bold animate-pulse">Recherche des catalyseurs en cours...</td></tr>
                                ) : filteredData.length === 0 ? (
                                    <tr><td colSpan="4" className="px-6 py-12 text-center text-slate-500 font-bold flex flex-col items-center justify-center"><AlertCircle className="mb-2 text-slate-400" size={32}/> Aucun événement trouvé pour cette période.</td></tr>
                                ) : (
                                    filteredData.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                            {activeTab === 'EARNINGS' ? (
                                                <>
                                                    <td className="px-6 py-4">
                                                        <p className="font-bold text-slate-900">{item.date}</p>
                                                        <p className="text-xs font-bold text-slate-500 mt-0.5">
                                                            {item.time === 'bmo' ? '🌅 Avant Bourse' : item.time === 'amc' ? '🌙 Après Bourse' : 'Heure inconnue'}
                                                        </p>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center font-black text-white text-xs shadow-sm">{item.symbol}</div>
                                                            <span className="font-bold text-slate-900 text-base">{item.symbol}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <p className="font-black text-indigo-600 text-base">{item.epsEstimated !== null ? item.epsEstimated.toFixed(2) : '-'}</p>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <p className="font-black text-slate-900 text-base">{formatCurrency(item.revenueEstimated)}</p>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="px-6 py-4 font-bold text-slate-900">{item.date}</td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center font-black text-white text-xs shadow-sm">{item.symbol}</div>
                                                            <span className="font-bold text-slate-900 text-base">{item.symbol}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <p className="font-black text-emerald-600 text-base">{formatCurrency(item.dividend)}</p>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold">{item.paymentDate || 'Non communiquée'}</span>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
